// assets/AllCourses/Exercises/PictureFillInTheBlanksExercise.jsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { toast } from "sonner";
import { useUploadCourseFileMutation } from "@/features/api/courseApi";
import { cn } from "@/utils/utils";

export default function PictureFillInTheBlanksExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResult = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";
  const [uploadFile] = useUploadCourseFileMutation();
  const completedRef = useRef(false);

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.pictureFillInTheBlanksQuestions)
    ? exercise.pictureFillInTheBlanksQuestions
    : [];

  /* ================= STUDENT STATE ================= */
  const [state, setState] = useState({
    answers: [],
    hasInteracted: false,
  });

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text || "").trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "");
  };

  /* ================= MEDIA ================= */
  const resolveMediaUrl = (url) => {
    if (!url) return "";
    const file = url.split("/").pop();
    return file ? `/uploads/image/${file}` : "";
  };

  /* ================= INIT / RESTORE ================= */
  useEffect(() => {
    if (!isPreview) return;

    const build = () =>
      questions.map((q) =>
        Array((q.sentence?.match(/_+/g) || []).length).fill("")
      );

    if (!exercise.attempt) {
      const fresh = build();
      setState({ answers: fresh, hasInteracted: false });
      setAttempt({ answers: fresh, hasInteracted: false });
      return;
    }

    if (Array.isArray(exercise.attempt.answers)) {
      const synced = questions.map((q, i) => {
        const blanks = (q.sentence?.match(/_+/g) || []).length;
        const saved = exercise.attempt.answers[i] || [];
        return Array.from({ length: blanks }, (_, j) => saved[j] || "");
      });
      setState({
        answers: synced,
        hasInteracted: !!exercise.attempt.hasInteracted,
      });
      return;
    }

    const fresh = build();
    setState({ answers: fresh, hasInteracted: false });
  }, [isPreview, questions, exercise.attempt, setAttempt]);

  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise?._id, isPreview]);

  /* ================= EDIT MODE ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("pictureFillInTheBlanksQuestions", next);
  };

  const uploadImage = async (file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await uploadFile(fd).unwrap();
      return res.fileUrl || res.url || "";
    } catch {
      toast.error("Image upload failed");
      return "";
    }
  };

  const updateQuestion = (qIdx, patch) => {
    const next = [...questions];
    next[qIdx] = { ...next[qIdx], ...patch };
    updateQuestions(next);
  };

  /* ================= STUDENT INPUT ================= */
  const handleInputChange = (qIdx, bIdx, value) => {
    if (isEditMode || showResult) return;

    setState((prev) => {
      const next = structuredClone(prev);
      next.answers[qIdx][bIdx] = value;
      next.hasInteracted = true;

      setAttempt(next);

      const allFilled = next.answers.every((q) =>
        q.every((a) => a.trim())
      );

      if (allFilled && !completedRef.current) {
        completedRef.current = true;

        const allCorrect = questions.every((q, qi) =>
          q.answers.every(
            (ans, ai) =>
              normalize(next.answers[qi][ai], q.requireCapitalization) ===
              normalize(ans, q.requireCapitalization)
          )
        );

        onComplete(exercise._id, allCorrect);
      }

      return next;
    });
  };

  /* ================= EDIT UI ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => {
          const blanks = (q.sentence?.match(/_+/g) || []).length;

          return (
            <Card key={qIdx} className="p-6 space-y-6">
              <TextareaField
                label={`Sentence ${qIdx + 1} (use ___ for blanks)`}
                value={q.sentence || ""}
                onChange={(v) => {
                  const count = (v.match(/_+/g) || []).length;
                  updateQuestion(qIdx, {
                    sentence: v,
                    answers: Array.from(
                      { length: count },
                      (_, i) => q.answers?.[i] || ""
                    ),
                  });
                }}
                rows={4}
                required
              />

              <div className="space-y-3">
                <label className="font-medium">Image (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const url = await uploadImage(file);
                    if (url) updateQuestion(qIdx, { image: url });
                  }}
                />

                {q.image && (
                  <img
                    src={resolveMediaUrl(q.image)}
                    className="max-w-xs rounded-lg border"
                  />
                )}
              </div>

              <div className="space-y-3">
                <label className="font-medium">Correct Answers</label>
                {Array.from({ length: blanks }).map((_, i) => (
                  <StringField
                    key={i}
                    label={`Blank ${i + 1}`}
                    value={q.answers[i] || ""}
                    onChange={(v) => {
                      const next = [...q.answers];
                      next[i] = v;
                      updateQuestion(qIdx, { answers: next });
                    }}
                  />
                ))}
              </div>

              <CapitalizationRule
                checked={!!q.requireCapitalization}
                onChange={(c) =>
                  updateQuestion(qIdx, { requireCapitalization: c })
                }
              />

              <Button
                size="sm"
                variant="destructive"
                onClick={() =>
                  updateQuestions(
                    questions.filter((_, i) => i !== qIdx)
                  )
                }
              >
                Remove Question
              </Button>
            </Card>
          );
        })}

        <Button
          variant="outline"
          onClick={() =>
            updateQuestions([
              ...questions,
              {
                sentence: "",
                answers: [],
                image: "",
                requireCapitalization: false,
              },
            ])
          }
        >
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= STUDENT UI ================= */
  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="text-base">
          Picture Fill in the Blanks
        </CardTitle>
        <CardDescription>
          Look at the image and complete the sentence.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-12">
        {questions.map((q, qIdx) => {
          const parts = q.sentence.split(/_+/g);
          const blanks = parts.length - 1;
          const answers = state.answers[qIdx] || [];

          return (
            <div key={qIdx} className="space-y-8">
              {q.image && (
                <div className="flex justify-center">
                  <img
                    src={resolveMediaUrl(q.image)}
                    className="max-h-80 rounded-xl border shadow"
                  />
                </div>
              )}

              <div className="p-8 bg-muted/40 rounded-xl text-lg flex flex-wrap justify-center items-center gap-3">
                {parts.map((p, i) => (
                  <React.Fragment key={i}>
                    <span>{p}</span>
                    {i < blanks && (
                      <Input
                        value={answers[i] || ""}
                        onChange={(e) =>
                          handleInputChange(qIdx, i, e.target.value)
                        }
                        disabled={showResult}
                        className={cn(
                          "w-36 text-center font-semibold",
                          showResult &&
                            normalize(
                              answers[i],
                              q.requireCapitalization
                            ) ===
                              normalize(
                                q.answers[i],
                                q.requireCapitalization
                              ) &&
                            "border-green-500 bg-green-50",
                          showResult &&
                            answers[i] &&
                            normalize(
                              answers[i],
                              q.requireCapitalization
                            ) !==
                              normalize(
                                q.answers[i],
                                q.requireCapitalization
                              ) &&
                            "border-red-500 bg-red-50"
                        )}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {showResult && (
                <div className="text-center space-y-2">
                  {q.answers.map((ans, i) => {
                    const correct =
                      normalize(answers[i], q.requireCapitalization) ===
                      normalize(ans, q.requireCapitalization);
                    return (
                      <p
                        key={i}
                        className={cn(
                          "font-medium",
                          correct
                            ? "text-green-700"
                            : "text-red-700"
                        )}
                      >
                        Blank {i + 1}:{" "}
                        {correct ? "Correct" : `Correct: ${ans}`}
                      </p>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
